import React from 'react';
import { Typography, Tag, Empty } from 'antd';

const { Text } = Typography;

export const formatText = (text: string): string => {
  if (!text) return '';
  return text
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

interface JsonViewerProps {
  data?: any;
  level?: number;
}

const JsonViewer: React.FC<JsonViewerProps> = ({ data, level = 0 }) => {
  if (!data || Object.keys(data).length === 0) {
    return level === 0 ? (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Metadata" />
    ) : null;
  }

  const renderRow = (label: string, value: any, subLevel: number) => {
    const isObject = typeof value === 'object' && value !== null;
    const isArray = Array.isArray(value);
    const isOcrObject = isObject && 'value' in value;

    if (!isObject || isOcrObject) {
      const displayValue = isOcrObject ? value.value : value;
      const confidence =
        isOcrObject && value.confidence ? value.confidence : null;

      return (
        <div
          key={label}
          className="metadata-row"
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(150px, 200px) 1fr auto',
            gap: '12px',
            padding: '8px 12px',
            borderBottom: '1px solid #f0f0f0',
            transition: 'background 0.2s',
            alignItems: 'start',
          }}
        >
          <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
            {formatText(label)}
          </Text>
          <Text
            style={{
              fontFamily: 'SFMono-Regular, Consolas, monospace',
              fontSize: '13px',
              color: '#262626',
              wordBreak: 'break-word',
            }}
          >
            {displayValue === null || displayValue === undefined
              ? '-'
              : String(displayValue)}
          </Text>
          {confidence && (
            <Tag
              color={confidence > 0.8 ? 'success' : 'warning'}
              style={{ margin: 0, fontSize: '10px' }}
            >
              {Math.round(confidence * 100)}%
            </Tag>
          )}
        </div>
      );
    }

    if (isArray) {
      return (
        <div key={label} style={{ marginBottom: '16px' }}>
          <div
            style={{
              padding: '8px 12px',
              background: '#f5f5f5',
              borderBottom: '1px solid #e8e8e8',
            }}
          >
            <Text strong style={{ fontSize: '12px', color: '#595959' }}>
              {formatText(label).toUpperCase()}
            </Text>
            <Tag style={{ marginLeft: 8 }}>{value.length} Items</Tag>
          </div>
          <div style={{ paddingLeft: '12px', borderLeft: '2px solid #f0f0f0' }}>
            {value.map((item: any, idx: number) => (
              <div
                key={idx}
                style={{
                  margin: '8px 0',
                  border: '1px solid #f0f0f0',
                  borderRadius: '4px',
                  background: '#fff',
                }}
              >
                <JsonViewer data={item} level={subLevel + 1} />
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div key={label} style={{ marginBottom: '12px' }}>
        <div style={{ padding: '4px 12px' }}>
          <Text strong style={{ fontSize: '12px', color: '#1890ff' }}>
            {formatText(label)}
          </Text>
        </div>
        <div style={{ marginLeft: '12px', borderLeft: '2px solid #e6f7ff' }}>
          <JsonViewer data={value} level={subLevel + 1} />
        </div>
      </div>
    );
  };

  return (
    <div style={{ background: level === 0 ? '#fff' : 'transparent' }}>
      <style>
        {`
                .metadata-row:hover {
                    background-color: #f0f7ff !important;
                }
                .metadata-row:last-child {
                    border-bottom: none !important;
                }
            `}
      </style>
      {Object.entries(data).map(([key, value]) => renderRow(key, value, level))}
    </div>
  );
};

export default JsonViewer;
